<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from '@/components/validations';
import General from '@/services/General';
import Swal from "sweetalert2";

export default {
  components: { validationMessages },

  validations: {
    changelogData: {
      version: { required },
      change: { required },
      type: { required }
    }
  },

  data() {
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      isEdit: false,
      isTransfer: false,

      changelogData: {
        id: null,
        version: '',
        change: '',
        ticket: '',
        type: 'tenant',
        commit_hash: ''
      },

      typeOptions: [
        { text: 'Tenant', value: 'tenant' },
        { text: 'Admin', value: 'admin' }
      ]
    };
  },

  methods: {
    openModal(editData = null, transferData = null) {
      this.showModal = true;
      this.submitted = false;
      this.isEdit = !!editData;
      this.isTransfer = !!transferData;

      // Editing existing record
      if (editData) {
        if (!editData.id) {
          this.failedmsg("Invalid changelog entry: ID is missing");
          this.showModal = false;
          return;
        }

        this.changelogData = {
          id: editData.id,
          version: editData.version || '',
          change: editData.change || '',
          ticket: editData.ticket || '',
          type: editData.type || 'tenant',
          commit_hash: editData.commit_hash || ''
        };
        return;
      }

      // Transfer mode
      if (transferData) {
        this.changelogData = {
          id: null,
          version: '',
          change: transferData.commit_message || '',
          ticket: '',
          type: 'tenant',
          commit_hash: transferData.commit_hash || ''
        };
        return;
      }

      // Default empty form (create)
      this.resetForm();
    },

    resetForm() {
      this.changelogData = {
        id: null,
        version: '',
        change: '',
        ticket: '',
        type: 'tenant',
        commit_hash: ''
      };

      this.isTransfer = false;
      this.submitted = false;
      this.$v.$reset();
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    async submitForm() {
      this.submitted = true;
      this.tryingToSubmit = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      try {
        const payload = {
          version: this.changelogData.version,
          change: this.changelogData.change,
          ticket: this.changelogData.ticket || null,
          type: this.changelogData.type,
          commit_hash: this.changelogData.commit_hash || null
        };

        let response;

        if (this.isEdit) {
          payload.id = this.changelogData.id;

          if (!payload.id) {
            this.failedmsg("Invalid changelog entry: ID is missing");
            this.tryingToSubmit = false;
            return;
          }

          response = await General.updateDashboardChangelog(payload);
        } else {
          response = await General.createDashboardChangelog(payload);
        }

        // Success
        if (response?.data?.data) {
          Swal.fire(
            "Success",
            this.isEdit ? "Changelog updated successfully" : "Changelog created successfully",
            "success"
          );
          this.$emit("onRefresh");
          this.closeModal();
          return;
        }

        // Handle error response (non-200 but not thrown)
        if (response?.data?.error) {
          const errorMsg = Array.isArray(response.data.error) 
            ? response.data.error.join(', ') 
            : response.data.error;
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMsg,
            confirmButtonColor: '#d33'
          });
          this.tryingToSubmit = false;
          return;
        }

      } catch (error) {
        let message = "An error occurred";
        const data = error.response?.data;

        // Laravel 422 – your custom error message
        if (error.response?.status === 422 && data?.error) {
          message = Array.isArray(data.error) 
            ? data.error.join(', ') 
            : data.error;
        }
        // Normal Laravel validation errors
        else if (data?.errors) {
          message = Object.values(data.errors).flat().join(", ");
        }
        // Standard error string
        else if (data?.error) {
          message = Array.isArray(data.error) 
            ? data.error.join(', ') 
            : data.error;
        }
        // "message" fallback
        else if (data?.message) {
          message = Array.isArray(data.message) 
            ? data.message.join(', ') 
            : data.message;
        }
        // JS error fallback
        else if (error.message) {
          message = error.message;
        }

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: message,
          confirmButtonColor: '#d33'
        });
      } finally {
        this.tryingToSubmit = false;
      }
    },

    failedmsg(message) {
      this.$bvToast.toast(message, {
        title: "Error",
        variant: "danger",
        solid: true
      });
    }
  }
};
</script>
<template>
  <b-modal
    id="add_edit_changelog"
    v-model="showModal"
    @hidden="resetForm"
    :title="isEdit ? 'Edit Dashboard Changelog' : (isTransfer ? 'Transfer to Dashboard Changelog' : 'Add Dashboard Changelog')"
    title-class="font-18"
    size="lg"
  >
    <form @submit.prevent="submitForm">
      <div class="row">
        <div class="col-12 mb-3">
          <b-form-group label="Version" label-for="version-input">
            <b-form-input
              v-model.trim="changelogData.version"
              type="text"
              id="version-input"
              placeholder="e.g., 20251125"
            ></b-form-input>
            <validationMessages
              v-if="submitted"
              :fieldName="'Version'"
              :validationName="$v.changelogData.version"
            ></validationMessages>
          </b-form-group>
        </div>

        <div class="col-12 mb-3">
          <b-form-group label="Change Description" label-for="change-input">
            <b-form-textarea
              v-model.trim="changelogData.change"
              id="change-input"
              rows="3"
              placeholder="Describe the change..."
            ></b-form-textarea>
            <validationMessages
              v-if="submitted"
              :fieldName="'Change Description'"
              :validationName="$v.changelogData.change"
            ></validationMessages>
          </b-form-group>
        </div>

        <div class="col-12 col-md-6 mb-3">
          <b-form-group label="Ticket (Optional)" label-for="ticket-input">
            <b-form-input
              v-model.trim="changelogData.ticket"
              type="text"
              id="ticket-input"
              placeholder="e.g., AZS-3434"
            ></b-form-input>
          </b-form-group>
        </div>

        <div class="col-12 col-md-6 mb-3">
          <b-form-group label="Type" label-for="type-input">
            <b-form-select
              v-model="changelogData.type"
              :options="typeOptions"
              id="type-input"
            ></b-form-select>
            <validationMessages
              v-if="submitted"
              :fieldName="'Type'"
              :validationName="$v.changelogData.type"
            ></validationMessages>
          </b-form-group>
        </div>
      </div>
    </form>

    <template #modal-footer>
      <b-button variant="secondary" @click="closeModal" :disabled="tryingToSubmit">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        @click="submitForm"
        :disabled="tryingToSubmit"
      >
        <b-spinner v-if="tryingToSubmit" small class="me-2"></b-spinner>
        {{ isEdit ? 'Update' : 'Create' }}
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>
</style>

